import React, { useState, useEffect } from 'react';
import RomanNumeralText from '../components/RomanNumeralText';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ResponsiveContainer } from 'recharts';
import { useChowaTheme } from '../config/chowaThemeContext';
import { commonStyles } from '../config/ChowaDashboardLayout';
import classNames from 'classnames';
import Card from '../config/ChowaCard';


// カスタムツールチップ
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div style={{ background: '#fff', border: '1px solid #ccc', borderRadius: 8, padding: 10, fontFamily: 'Segoe UI, Roboto, Arial, sans-serif' }}>
        <table>
          <tbody>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>CustomUnitName:</td>
              <td>{item.customData || '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Unit Serial:</td>
              <td>{item.unitSerialNumber && item.unitSerialNumber !== '' ? item.unitSerialNumber : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Device ID:</td>
              <td>{item.deviceId && item.deviceId !== '' ? item.deviceId : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Last Update:</td>
              <td>{item.timestamp ? new Date(item.timestamp).toLocaleString('ja-JP') : '-'}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', paddingRight: 8 }}>Operating Time:</td>
              <td>{item.hours !== undefined && item.hours !== null && item.hours !== '' ? item.hours + 'h' : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
  return null;
};

// データを指定されたサイズでチャンクに分割する関数
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const AllEngineHoursBarChart = ({ data, seriesColor, selectedModel }) => {
  const { isDark, getCurrentTheme } = useChowaTheme();
  const currentTheme = getCurrentTheme();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // チャートバーの色（シリーズカラーを使用）
  const barColor = seriesColor || currentTheme.primary;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!data || data.length === 0) {
    return (
      <Card className={commonStyles.card.padding.default}>
        <div className="flex flex-col items-center justify-center h-[400px]">
          <div className={classNames(commonStyles.text.title, "text-4xl text-gray-400 dark:text-gray-600 mb-2")}>ー</div>
          <div className={classNames(commonStyles.text.subtitle, "text-gray-400 dark:text-gray-600")}>Data not found</div>
        </div>
      </Card>
    );
  }

  // モデル名を短縮形に変換（unit#0 → #0）
  const processedData = data.map(item => {
    const shortName = item.name.replace(/^unit/, '');
    return {
      ...item,
      name: shortName,  // チャート表示用
      originalName: item.name,  // ツールチップ用
      // 稼働時間が0の場合は-を表示するように修正
      displayHours: item.hours !== undefined && item.hours !== null && item.hours !== '' && item.hours > 0 ? item.hours : '-'
    };
  });

  // データを50個ずつのチャンクに分割
  const dataChunks = chunkArray(processedData, 50);

  // 1400以下は斜めにmodel名表示
  const isMobile = windowWidth < 1400;

  const xAxisTickProps = {
    fill: isDark ? '#FFFFFF' : '#1F2937',
    fontSize: isMobile ? 10 : 14,
    angle: -45,
    textAnchor: 'end',
    dy: 2,
  };

  const chartMargin = {
    top: 15,
    right: 10,  // 右マージン縮小
    left: 5,    // 左マージン縮小
    bottom: isMobile ? 35 : 5,  // 下マージンも少し縮小
  };

  // データの最初のModel名から#番号を抽出
  const getStartNumber = () => {
    if (!processedData || processedData.length === 0) return 1;
    const match = processedData[0].name && processedData[0].name.match(/#(\d+)$/);
    return match ? parseInt(match[1], 10) : 1;
  };
  const startNumber = getStartNumber();

  return (
    <Card className={commonStyles.layout.section} style={{ borderColor: seriesColor, padding: '20px' }}>
      <h2 className={classNames(commonStyles.text.title, "text-center text-lg sm:text-2xl lg:text-3xl")} style={{ color: seriesColor, marginBottom: '20px', fontWeight: '600' }}><RomanNumeralText text={selectedModel} /> ALL View</h2>
      
      {dataChunks.map((chunkData, chunkIndex) => {
        const chunkStart = startNumber + chunkIndex * 50;
        const chunkEnd = chunkStart + chunkData.length - 1;
        
        return (
          <div key={chunkIndex} className="mb-8">
            <h3 className={classNames(commonStyles.text.subtitle, "text-center mb-4")}>#{chunkStart} - #{chunkEnd} ({chunkData.length}個)</h3>
            
            <div className="min-w-0">
              <ResponsiveContainer 
                width="100%" 
                height={450} 
                className="md:h-[550px] lg:h-[650px]"
              >
                <BarChart
                  data={chunkData}
                  margin={chartMargin}
                  barSize={30}  // バーサイズを小さくして、より多くのバーが収まるように
                  className="md:barSize-35 lg:barSize-40"
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="#374151"
                    opacity={0.1}
                  />
                  <XAxis 
                    dataKey="name"
                    tick={xAxisTickProps}
                    axisLine={{ stroke: '#4B5563' }}
                    padding={{ left: 10, right: 10 }}  // パディング縮小でより密に配置
                    className="sm:text-sm"
                    interval={0}  // 全てのラベルを表示
                    tickFormatter={(name) => {
                      const match = name && name.match(/#\d+$/);
                      return match ? match[0] : name;
                    }}
                  />
                  <YAxis 
                    tick={{ fill: isDark ? '#FFFFFF' : '#1F2937', fontSize: 10 }}  // Y軸数値サイズ縮小
                    axisLine={{ stroke: '#4B5563' }}
                    label={{ 
                      value: 'Operating time (h)', 
                      angle: -90, 
                      position: 'insideLeft', 
                      fill: isDark ? '#FFFFFF' : '#1F2937',
                      fontSize: 14,  // Y軸ラベルサイズ縮小
                      dx: 5,
                      dy: 70,
                      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                    }}
                  />
                  <Tooltip 
                    content={<CustomTooltip />}
                    contentStyle={{
                      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      color: isDark ? '#FFFFFF' : '#1F2937',
                      fontSize: '12px'
                    }}
                  />
                  <Bar 
                    dataKey="hours" 
                    fill={barColor}
                    radius={[4, 4, 0, 0]}
                  >
                                         <LabelList 
                       dataKey="displayHours" 
                       position="top" 
                       fill={isDark ? '#FFFFFF' : '#1F2937'}
                       fontSize={10}  // 数値ラベルのサイズを小さく
                       formatter={(value) => value === '-' ? '-' : `${Math.floor(value)}h`}
                     />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      })}
    </Card>
  );
};

export default AllEngineHoursBarChart; 